import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

// ============================================================================
// SiteConditionsCard
//
// Renders 3 dropdowns (floor_level, access, truck_distance) + 1 textarea
// (site_notes) in a sidebar card.
//
// Dropdowns: save immediately on change via PUT /api/quotes/:id.
// Textarea:  debounced save 800ms after the user stops typing (avoids one
//            PUT per keystroke).
//
// Props:
//   quoteId  - number, the quote being edited
//   value    - { site_floor_level, site_access, site_truck_distance, site_notes }
//   onSaved  - () => void, called after a successful save so the parent
//              can re-fetch the quote
//
// Enum values must mirror migration 004 CHECK constraints AND the backend
// validators in quotesController.update.
// ============================================================================

const FLOOR_LEVELS = [
  { value: 'ground',     label: 'Ground floor' },
  { value: 'second',     label: '2nd floor' },
  { value: 'third_plus', label: '3rd floor or higher' },
];

const ACCESS_OPTIONS = [
  { value: 'street',     label: 'Street access (standard)' },
  { value: 'difficult',  label: 'Difficult access' },
  { value: 'cliff_edge', label: 'Cliff edge / steep terrain' },
  { value: 'off_road',   label: 'Off-road / no vehicle access' },
];

const TRUCK_DISTANCES = [
  { value: 'close',  label: 'Close (truck can park near install)' },
  { value: 'medium', label: 'Medium (some carrying required)' },
  { value: 'far',    label: 'Far (significant carrying required)' },
];

export default function SiteConditionsCard({ quoteId, value, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Local state for the notes textarea (debounced save).
  const [notesDraft, setNotesDraft] = useState(value?.site_notes || '');
  const notesTimerRef = useRef(null);

  // Defaults shown when the quote has never had site_conditions set.
  // We DO NOT save these defaults until the user actually picks something -
  // a NULL in the DB is meaningful (means "estimator hasn't told us yet").
  const floor    = value?.site_floor_level    || '';
  const access   = value?.site_access         || '';
  const distance = value?.site_truck_distance || '';

  const saveField = async (fieldName, fieldValue) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/quotes/${quoteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('mq_token')}`,
        },
        body: JSON.stringify({ [fieldName]: fieldValue }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Save failed');
      }
      toast.success('Site conditions updated');
      setLastSaved(new Date());
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Sync local draft when the parent re-fetches with a different value.
  useEffect(() => {
    setNotesDraft(value?.site_notes || '');
  }, [value?.site_notes]);

  // Debounced save: 800ms after the user stops typing.
  useEffect(() => {
    // Skip if draft equals what's already saved (avoids a save on mount).
    if ((value?.site_notes || '') === notesDraft) return;
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    notesTimerRef.current = setTimeout(() => {
      saveField('site_notes', notesDraft);
    }, 800);
    return () => {
      if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    };
    // eslint-disable-next-line
  }, [notesDraft]);

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-header">
        <span className="card-title">Site Conditions</span>
        {lastSaved && (
          <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 10 }}>
            Saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      <div style={{ fontSize: 12 }}>
        <div style={{ marginBottom: 12 }}>
          <label className="form-label">Floor level</label>
          <select
            className="form-select"
            style={{ width: '100%', fontSize: 12 }}
            value={floor}
            disabled={saving}
            onChange={e => saveField('site_floor_level', e.target.value)}
          >
            <option value="" disabled>Select floor level...</option>
            {FLOOR_LEVELS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label className="form-label">Site access</label>
          <select
            className="form-select"
            style={{ width: '100%', fontSize: 12 }}
            value={access}
            disabled={saving}
            onChange={e => saveField('site_access', e.target.value)}
          >
            <option value="" disabled>Select access type...</option>
            {ACCESS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="form-label">Truck distance to install</label>
          <select
            className="form-select"
            style={{ width: '100%', fontSize: 12 }}
            value={distance}
            disabled={saving}
            onChange={e => saveField('site_truck_distance', e.target.value)}
          >
            <option value="" disabled>Select distance...</option>
            {TRUCK_DISTANCES.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div style={{ marginTop: 12 }}>
          <label className="form-label">Site notes</label>
          <textarea
            className="form-input"
            style={{ width: '100%', fontSize: 12, minHeight: 60, fontFamily: 'inherit' }}
            placeholder="Gate codes, on-site contacts, parking instructions..."
            value={notesDraft}
            onChange={e => setNotesDraft(e.target.value)}
          />
        </div>

        {(!floor || !access || !distance) && (
          <div style={{
            marginTop: 12,
            padding: 8,
            background: 'var(--bg-2, #f7f7f7)',
            borderRadius: 4,
            fontSize: 11,
            color: 'var(--text-3)',
          }}>
            (i) Site conditions help Operations plan equipment and crew. Fill in
            all three before sending to install.
          </div>
        )}
      </div>
    </div>
  );
}